import { dspAssetUrl } from './dspAssetUrl';
import { ampModel, normalizeAmpParams } from './ampPresets';

const ROOT = '/audio/nam/v1/';
const workletLoads = new WeakMap();
const captureLoads = new Map();
let moduleLoad;
const decibels = db => 10 ** (db / 20);
export const ampLatencySeconds = rate => rate === 48000 ? 0 : 64 / rate;

async function fetchAsset(path, type) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 20000);
  try {
    const response = await fetch(dspAssetUrl(path), { mode: 'cors', credentials: 'omit', signal: controller.signal });
    if (!response.ok) throw new Error(`Amp download failed (${response.status}). Retry playback.`);
    return await response[type]();
  } finally { clearTimeout(timer); }
}

function loadModule() {
  if (!moduleLoad) moduleLoad = fetchAsset(`${ROOT}nam.wasm`, 'arrayBuffer')
    .then(bytes => WebAssembly.compile(bytes))
    .catch(error => { moduleLoad = null; throw error; });
  return moduleLoad;
}
function loadCapture(id) {
  if (!captureLoads.has(id)) captureLoads.set(id, fetchAsset(`${ROOT}models/${ampModel(id).file}`, 'text')
    .then(text => {
      const model = JSON.parse(text);
      if (model.sample_rate !== 48000 || model.architecture !== 'WaveNet') throw new Error('Unsupported amp capture.');
      return text;
    }).catch(error => { captureLoads.delete(id); throw error; }));
  return captureLoads.get(id);
}

/** Builds the identical insert for live playback and OfflineAudioContext. */
export async function createAmpGraph(context, savedParams) {
  const params = normalizeAmpParams(savedParams);
  if (!context?.audioWorklet || typeof globalThis.AudioWorkletNode !== 'function') {
    throw new Error('CU-AMP needs AudioWorklet support. Use a current browser or turn the amp off.');
  }
  if (context.sampleRate < 8000 || context.sampleRate > 192000) throw new Error('Unsupported amp sample rate.');
  if (!workletLoads.has(context)) workletLoads.set(context, context.audioWorklet.addModule(dspAssetUrl('/audio/ampProcessor.js'))
    .catch(error => { workletLoads.delete(context); throw error; }));
  const [, module, modelJson] = await Promise.all([workletLoads.get(context), loadModule(), loadCapture(params.model)]);
  const node = new globalThis.AudioWorkletNode(context, 'cookup-amp', {
    numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [2],
    channelCount: 2, channelCountMode: 'explicit',
    processorOptions: { module, modelJson }
  });
  let runtimeError = null;
  try {
    await new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('Amp initialization timed out. Retry playback.')), 20000);
      node.port.onmessage = ({ data }) => {
        if (data?.type === 'ready') { clearTimeout(timer); resolve(); }
        if (data?.type === 'error') { clearTimeout(timer); runtimeError = new Error(data.message); reject(runtimeError); }
      };
      node.onprocessorerror = () => { clearTimeout(timer); runtimeError = new Error('Amp processing stopped. Turn it off and on to retry.'); reject(runtimeError); };
    });
  } catch (error) { node.port.postMessage({ type: 'dispose' }); node.disconnect(); throw error; }

  const input = context.createGain();
  const pre = context.createGain();
  const output = context.createGain();
  const wet = context.createGain();
  const dry = context.createGain();
  const dryDelay = context.createDelay(0.02);
  dryDelay.delayTime.value = ampLatencySeconds(context.sampleRate);
  const bass = context.createBiquadFilter(); bass.type = 'lowshelf'; bass.frequency.value = 160;
  const mid = context.createBiquadFilter(); mid.type = 'peaking'; mid.frequency.value = 800; mid.Q.value = 0.7;
  const treble = context.createBiquadFilter(); treble.type = 'highshelf'; treble.frequency.value = 3200;
  const dc = context.createBiquadFilter(); dc.type = 'highpass'; dc.frequency.value = 20; dc.Q.value = 0.707;
  input.connect(pre); pre.connect(node); node.connect(dc); dc.connect(bass); bass.connect(mid); mid.connect(treble); treble.connect(wet); wet.connect(output);
  input.connect(dryDelay); dryDelay.connect(dry); dry.connect(output);
  const nodes = [input, pre, node, dc, bass, mid, treble, wet, dryDelay, dry, output];
  let disposed = false;
  const graph = {
    input, output, node, latencySeconds: ampLatencySeconds(context.sampleRate),
    get error() { return runtimeError; },
    setParams(next, initial = false) {
      const p = normalizeAmpParams(next);
      const set = (param, value) => initial ? (param.value = value) : param.setTargetAtTime(value, context.currentTime, 0.015);
      set(pre.gain, decibels(p.inputDb));
      set(bass.gain, p.bass); set(mid.gain, p.mid); set(treble.gain, p.treble);
      set(wet.gain, p.mix * decibels(ampModel(params.model).trimDb));
      set(dry.gain, 1 - p.mix); set(output.gain, decibels(p.outputDb));
    },
    disconnect() {
      if (disposed) return;
      disposed = true;
      node.port.postMessage({ type: 'dispose' });
      nodes.forEach(entry => { try { entry.disconnect(); } catch (_) {} });
    }
  };
  graph.setParams(params, true);
  return graph;
}
